import {
  BuildingType,
  MechanicalSpaces,
  ParkingLotType,
} from '../../../models/ParkingService.ts';
import { ExistenceStatus } from '../../../models/PlaceService.ts';
import { useForm } from '@mantine/form';
import {
  Button,
  Checkbox,
  Group,
  Stack,
  TagsInput,
  TextInput,
} from '@mantine/core';
import React from 'react';

export interface MutateParkingServiceParkingInfoFormValues {
  mechanicalSpaces: MechanicalSpaces | null;
  buildingTypes: BuildingType[];
  parkingLotTypes: ParkingLotType[];
  hasRestroom: ExistenceStatus;
  hasElevator: ExistenceStatus;
  hasParkingBooth: ExistenceStatus;
  floorLevels: string[];
  heightLimit: string;
}

interface MutateParkingServiceParkingInfoFormProps {
  initialValues: MutateParkingServiceParkingInfoFormValues;
  onSubmit: (formValues: MutateParkingServiceParkingInfoFormValues) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitButtonText?: string;
}

export default function MutateParkingServiceParkingInfoForm({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting,
  submitButtonText,
}: MutateParkingServiceParkingInfoFormProps) {
  const form = useForm<MutateParkingServiceParkingInfoFormValues>({
    mode: 'controlled',
    initialValues: {
      mechanicalSpaces: initialValues.mechanicalSpaces,
      buildingTypes: initialValues.buildingTypes,
      parkingLotTypes: initialValues.parkingLotTypes,
      hasRestroom: initialValues.hasRestroom,
      hasElevator: initialValues.hasElevator,
      hasParkingBooth: initialValues.hasParkingBooth,
      floorLevels: initialValues.floorLevels,
      heightLimit: initialValues.heightLimit,
    },
    validate: {
      floorLevels: (value) => {
        const hasDuplicates = new Set(value).size !== value.length;
        return hasDuplicates ? '樓層重複' : null;
      },
      heightLimit: (value) => {
        if (value === '') return null;
        const num = Number(value);
        if (!isNaN(num) && (num === -1 || num > 0)) return null;
        return '限高值只能為留空（未知）、-1（不限）或正數';
      },
    },
  });

  const handleSubmit = form.onSubmit((values) => {
    onSubmit(values);
  });

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  const transformFloorLevel = (value: string): string => {
    if (!/^-?\d+(\.5)?$/.test(value)) return value; // 只接受整數或 .5

    const num = parseFloat(value);
    const isMezzanine = value.endsWith('.5');
    const base = Math.floor(Math.abs(num));

    if (isMezzanine) {
      return num >= 0 ? `${base}M` : `B${base}M`;
    }

    return num >= 0 ? `${num}F` : `B${Math.abs(num)}`;
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Stack gap={16}>
          <Group gap={16} style={{ paddingBottom: 8 }}>
            {/* 機械車位配置 */}
            <Stack gap={8}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  lineHeight: '20px',
                }}
              >
                機械車位配置
              </div>
              <Group gap={12} w={305.5}>
                {/* 全部機械 */}
                <Checkbox
                  radius={'xl'}
                  label={'全部機械'}
                  checked={form.values.mechanicalSpaces === 'ALL_MECHANICAL'}
                  onChange={() => {
                    form.setFieldValue(
                      'mechanicalSpaces',
                      form.values.mechanicalSpaces === 'ALL_MECHANICAL'
                        ? null
                        : 'ALL_MECHANICAL',
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                {/* 部分機械 */}
                <Checkbox
                  radius={'xl'}
                  label={'部分機械'}
                  checked={form.values.mechanicalSpaces === 'PARTLY_MECHANICAL'}
                  onChange={() => {
                    form.setFieldValue(
                      'mechanicalSpaces',
                      form.values.mechanicalSpaces === 'PARTLY_MECHANICAL'
                        ? null
                        : 'PARTLY_MECHANICAL',
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                {/* 無機械 */}
                <Checkbox
                  radius={'xl'}
                  label={'無機械'}
                  checked={form.values.mechanicalSpaces === 'NO_MECHANICAL'}
                  onChange={() => {
                    form.setFieldValue(
                      'mechanicalSpaces',
                      form.values.mechanicalSpaces === 'NO_MECHANICAL'
                        ? null
                        : 'NO_MECHANICAL',
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
              </Group>
            </Stack>

            {/* 室內外狀態 */}
            <Checkbox.Group
              label={'室內外狀態'}
              key={form.key('buildingTypes')}
              {...form.getInputProps('buildingTypes')}
              styles={{
                root: {
                  width: 305.5,
                },
                label: {
                  paddingBottom: 8,
                  lineHeight: '20px',
                },
              }}
            >
              <Group gap={12}>
                <Checkbox
                  value={'OUTDOOR'}
                  label={'室外無遮雨棚'}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                <Checkbox
                  value={'SHELTER'}
                  label={'室外有遮雨棚'}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                <Checkbox
                  value={'INDOOR'}
                  label={'室內'}
                  styles={{ label: { paddingLeft: 8 } }}
                />
              </Group>
            </Checkbox.Group>
          </Group>

          <Group gap={16} style={{ paddingBottom: 8 }}>
            {/* 停車場類型 */}
            <Checkbox.Group
              label={'停車場類型'}
              key={form.key('parkingLotTypes')}
              {...form.getInputProps('parkingLotTypes')}
              styles={{
                root: {
                  width: 305.5,
                },
                label: {
                  paddingBottom: 8,
                  lineHeight: '20px',
                },
              }}
            >
              <Group gap={12}>
                <Checkbox
                  value={'SINGLE_FLOOR'}
                  label={'平面'}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                <Checkbox
                  value={'MULTI_FLOOR'}
                  label={'立體'}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                <Checkbox
                  value={'UNDERGROUND'}
                  label={'地下'}
                  styles={{ label: { paddingLeft: 8 } }}
                />
              </Group>
            </Checkbox.Group>

            {/* 廁所 */}
            <Stack gap={8}>
              <div
                style={{ fontSize: 14, fontWeight: 500, lineHeight: '20px' }}
              >
                廁所
              </div>
              <Group gap={12} w={305.5}>
                {/* 有 */}
                <Checkbox
                  radius={'xl'}
                  label={'有'}
                  checked={form.values.hasRestroom === 'EXISTS'}
                  onChange={() => {
                    form.setFieldValue(
                      'hasRestroom',
                      form.values.hasRestroom === 'EXISTS'
                        ? 'UNKNOWN'
                        : 'EXISTS',
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                {/* 無 */}
                <Checkbox
                  radius={'xl'}
                  label={'無'}
                  checked={form.values.hasRestroom === 'NOT_EXIST'}
                  onChange={() => {
                    form.setFieldValue(
                      'hasRestroom',
                      form.values.hasRestroom === 'NOT_EXIST'
                        ? 'UNKNOWN'
                        : 'NOT_EXIST',
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
              </Group>
            </Stack>
          </Group>

          <Group gap={16} style={{ paddingBottom: 8 }}>
            {/* 電梯 */}
            <Stack gap={8}>
              <div
                style={{ fontSize: 14, fontWeight: 500, lineHeight: '20px' }}
              >
                電梯
              </div>
              <Group gap={12} w={305.5}>
                {/* 有 */}
                <Checkbox
                  radius={'xl'}
                  label={'有'}
                  checked={form.values.hasElevator === 'EXISTS'}
                  onChange={() => {
                    form.setFieldValue(
                      'hasElevator',
                      form.values.hasElevator === 'EXISTS'
                        ? 'UNKNOWN'
                        : 'EXISTS',
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                {/* 無 */}
                <Checkbox
                  radius={'xl'}
                  label={'無'}
                  checked={form.values.hasElevator === 'NOT_EXIST'}
                  onChange={() => {
                    form.setFieldValue(
                      'hasElevator',
                      form.values.hasElevator === 'NOT_EXIST'
                        ? 'UNKNOWN'
                        : 'NOT_EXIST',
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
              </Group>
            </Stack>

            {/* 管理室 */}
            <Stack gap={8}>
              <div
                style={{ fontSize: 14, fontWeight: 500, lineHeight: '20px' }}
              >
                管理室
              </div>
              <Group gap={12} w={305.5}>
                {/* 有 */}
                <Checkbox
                  radius={'xl'}
                  label={'有'}
                  checked={form.values.hasParkingBooth === 'EXISTS'}
                  onChange={() => {
                    form.setFieldValue(
                      'hasParkingBooth',
                      form.values.hasParkingBooth === 'EXISTS'
                        ? 'UNKNOWN'
                        : 'EXISTS',
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                {/* 無 */}
                <Checkbox
                  radius={'xl'}
                  label={'無'}
                  checked={form.values.hasParkingBooth === 'NOT_EXIST'}
                  onChange={() => {
                    form.setFieldValue(
                      'hasParkingBooth',
                      form.values.hasParkingBooth === 'NOT_EXIST'
                        ? 'UNKNOWN'
                        : 'NOT_EXIST',
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
              </Group>
            </Stack>
          </Group>

          {/* 車位樓層 */}
          <TagsInput
            label={'車位樓層'}
            placeholder={
              form.values.floorLevels.length !== 0
                ? ''
                : '請輸入數字 + Enter（夾層請以樓層數 ＋『.5』表示）'
            }
            key={form.key('floorLevels')}
            {...form.getInputProps('floorLevels')}
            onChange={(values) => {
              const transformed = values.map(transformFloorLevel);
              form.setFieldValue('floorLevels', transformed);
            }}
            styles={{
              label: { paddingBottom: 4, lineHeight: '20px' },
              input: { width: 627 },
            }}
          />

          {/* 限高 */}
          <TextInput
            label={'限高(cm)'}
            placeholder={'請輸入限高(cm)，若無限制請輸入 -1'}
            key={form.key('heightLimit')}
            {...form.getInputProps('heightLimit')}
            styles={{
              label: { paddingBottom: 4, lineHeight: '20px' },
              input: { width: 627 },
            }}
          />
        </Stack>

        {/* 取消 與 新增 or 更新按鈕 */}
        <Group justify={'flex-end'} mt={'24px'}>
          <Button variant="outline" onClick={handleCancel}>
            關閉
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            loaderProps={{ type: 'oval', size: 'xs' }}
          >
            {submitButtonText}
          </Button>
        </Group>
      </form>
    </>
  );
}
